import { AlertCircle, CheckCircle2, Info, AlertTriangle } from 'lucide-react';

// Inline alert banner for top-of-section messages (auth errors, success
// confirmations, info notices). For transient notifications, use Toast.
//
// Tones map to WCAG-AA-tested color pairs. role="alert" for errors so
// screen readers announce assertively; role="status" for the rest so
// they're announced politely (without interrupting).

const TONE = {
  error: {
    icon: AlertCircle,
    bg: 'bg-red-50',
    border: 'border-red-200',
    fg: 'text-red-800',
    iconFg: 'text-red-600',
  },
  success: {
    icon: CheckCircle2,
    bg: 'bg-green-50',
    border: 'border-green-200',
    fg: 'text-green-800',
    iconFg: 'text-green-600',
  },
  info: {
    icon: Info,
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    fg: 'text-blue-800',
    iconFg: 'text-blue-600',
  },
  warn: {
    icon: AlertTriangle,
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    fg: 'text-amber-800',
    iconFg: 'text-amber-600',
  },
};

const Alert = ({ tone = 'info', title = null, icon = null, className = '', children, ...rest }) => {
  const cfg = TONE[tone] || TONE.info;
  const Icon = icon || cfg.icon;
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={[
        'rounded-lg border p-3 flex items-start gap-2.5',
        cfg.bg,
        cfg.border,
        cfg.fg,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${cfg.iconFg}`} aria-hidden="true" />
      <div className="flex-1 text-sm leading-relaxed">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={title ? 'mt-1' : ''}>{children}</div>}
      </div>
    </div>
  );
};

// Simple text container for alert body content.
// Mirrors shadcn/ui's AlertDescription so callers can swap imports without changes.
export const AlertDescription = ({ className = '', children, ...rest }) => (
  <div className={['text-sm leading-relaxed', className].filter(Boolean).join(' ')} {...rest}>
    {children}
  </div>
);

export default Alert;
